import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'react-hot-toast';
import { Plus, DollarSign, Calendar, Search, Loader2, AlertCircle, Layers, X, Check } from 'lucide-react';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import ConditionRecordDialog from './condition-records/ConditionRecordDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ConditionRecords = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('pricing');
  const [viewItem, setViewItem] = useState(null);
  const [conditionRecords, setConditionRecords] = useState([]);
  const [conditionTypes, setConditionTypes] = useState([]);
  const [conditionTables, setConditionTables] = useState([]);
  const [accessSequences, setAccessSequences] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [customers, setCustomers] = useState([]);
  // New master data states for dropdowns
  const [salesOrgs, setSalesOrgs] = useState([]);
  const [distributionChannels, setDistributionChannels] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [plants, setPlants] = useState([]);
  const [materialGroups, setMaterialGroups] = useState([]);
  
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [filterType, setFilterType] = useState('');
  
  const [formData, setFormData] = useState({
    condition_type_id: '',
    condition_table_id: '',
    sales_org: '',
    distribution_channel: '',
    customer_id: '',
    material_id: '',
    material_ids: [], // Multiple materials for item-level
    material_group: '',
    material_groups: [], // Multiple material groups for item-level
    plant: '',
    plants: [], // Multiple plants for item-level
    condition_amount: '',
    condition_unit: '',
    condition_currency: 'USD',
    per_unit: 1,
    unit_of_measure: 'EA',
    has_scales: false,
    scale_basis: 'B',
    scale_lines: [],
    valid_from: new Date().toISOString().split('T')[0],
    valid_to: '2099-12-31',
    is_active: true
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadRecords();
  // eslint-disable-next-line
  }, [filterType]);

  // Update UoM default when condition type changes
  useEffect(() => {
    if (formData.condition_type_id) {
      const selectedType = conditionTypes.find(ct => ct.condition_type_id === formData.condition_type_id);
      if (selectedType) {
        const calcType = selectedType.calculation_type;
        const calcTypeDefaults = {
          'A': { uom: null, perUnit: null },     // Percentage
          'B': { uom: null, perUnit: null },     // Fixed Amount
          'C': { uom: 'EA', perUnit: 1 },        // Quantity
          'D': { uom: 'KG', perUnit: 1 },        // Gross Weight
          'E': { uom: 'KG', perUnit: 1 },        // Net Weight
          'F': { uom: 'M3', perUnit: 1 }         // Volume
        };
        const defaults = calcTypeDefaults[calcType] || calcTypeDefaults['C'];
        
        // Only update if the current value is empty or if switching to a disabled type
        if (!calcType || ['A', 'B'].includes(calcType)) {
          // For percentage and fixed amount, clear UoM and per_unit
          setFormData(prev => ({
            ...prev,
            unit_of_measure: '',
            per_unit: ''
          }));
        } else if (!formData.unit_of_measure || formData.unit_of_measure === '') {
          // For quantity-based types, set default UoM
          setFormData(prev => ({
            ...prev,
            unit_of_measure: defaults.uom,
            per_unit: defaults.perUnit || 1
          }));
        }
      }
    }
  // eslint-disable-next-line
  }, [formData.condition_type_id, conditionTypes]);

  // Get filtered condition tables based on selected condition type's access sequence
  const getFilteredConditionTables = () => {
    if (!formData.condition_type_id) {
      return []; // No tables if no condition type selected
    }
    
    const selectedConditionType = conditionTypes.find(ct => ct.condition_type_id === formData.condition_type_id);
    if (!selectedConditionType?.access_sequence_id) {
      return conditionTables; // Return all tables if no access sequence assigned
    }
    
    const accessSequence = accessSequences.find(as => as.access_sequence_id === selectedConditionType.access_sequence_id);
    if (!accessSequence?.steps || accessSequence.steps.length === 0) {
      return conditionTables; // Return all tables if access sequence has no steps
    }
    
    // Get condition table IDs from the access sequence steps
    const allowedTableIds = accessSequence.steps.map(step => step.condition_table_id);
    
    // Filter condition tables to only include those in the access sequence
    return conditionTables.filter(table => allowedTableIds.includes(table.condition_table_id));
  };

  // Get key fields for the selected condition table
  const getKeyFieldsForTable = () => {
    if (!formData.condition_table_id) return { headerFields: [], itemFields: [] };
    
    const selectedTable = conditionTables.find(t => t.condition_table_id === formData.condition_table_id);
    if (!selectedTable) return { headerFields: [], itemFields: [] };
    
    // If we have key_field_definitions (new format), use them
    if (selectedTable.key_field_definitions && selectedTable.key_field_definitions.length > 0) {
      const headerFields = selectedTable.key_field_definitions.filter(f => f.level === 'HEADER');
      const itemFields = selectedTable.key_field_definitions.filter(f => f.level === 'ITEM' || !f.level);
      return { headerFields, itemFields };
    }
    
    // Fall back to legacy key_fields array - use default level mapping
    const defaultLevelMap = {
      'customer_id': 'HEADER',
      'sales_org': 'HEADER',
      'distribution_channel': 'HEADER',
      'division': 'HEADER',
      'customer_group': 'HEADER',
      'price_list': 'HEADER',
      'material_id': 'ITEM',
      'material_group': 'ITEM',
      'plant': 'ITEM'
    };
    
    const labelMap = {
      'customer_id': 'Customer',
      'material_id': 'Material',
      'material_group': 'Material Group',
      'sales_org': 'Sales Org',
      'distribution_channel': 'Dist. Channel',
      'division': 'Division',
      'plant': 'Plant',
      'customer_group': 'Customer Group',
      'price_list': 'Price List'
    };
    
    const keyFields = selectedTable.key_fields || [];
    const headerFields = keyFields
      .filter(f => defaultLevelMap[f] === 'HEADER')
      .map(f => ({ field_id: f, field_label: labelMap[f] || f, level: 'HEADER' }));
    const itemFields = keyFields
      .filter(f => defaultLevelMap[f] !== 'HEADER')
      .map(f => ({ field_id: f, field_label: labelMap[f] || f, level: 'ITEM' }));
    
    return { headerFields, itemFields };
  };

  // Get calculation type info from selected condition type
  const getCalculationTypeInfo = () => {
    if (!formData.condition_type_id) return null;
    
    const selectedType = conditionTypes.find(ct => ct.condition_type_id === formData.condition_type_id);
    if (!selectedType) return null;
    
    const calcType = selectedType.calculation_type;
    
    // Define behavior for each calculation type
    const calcTypeConfig = {
      'A': { // Percentage
        label: 'Percentage',
        amountLabel: 'Percentage (%)',
        amountPlaceholder: 'e.g., 5, 10',
        amountSuffix: '%',
        currencyDisabled: true,
        perUnitDisabled: true,
        uomDisabled: true,  // UoM greyed out for percentage
        defaultCurrency: null,
        defaultPerUnit: null,
        defaultUoM: null
      },
      'B': { // Fixed Amount
        label: 'Fixed Amount',
        amountLabel: 'Fixed Amount',
        amountPlaceholder: 'e.g., 100.00',
        amountSuffix: null,
        currencyDisabled: true, // Uses company code currency
        perUnitDisabled: true,
        uomDisabled: true,  // UoM greyed out for fixed amount
        defaultCurrency: 'USD', // Company code currency
        defaultPerUnit: null,
        defaultUoM: null
      },
      'C': { // Quantity
        label: 'Quantity',
        amountLabel: 'Amount per Unit',
        amountPlaceholder: 'e.g., 10.00',
        amountSuffix: null,
        currencyDisabled: false,
        perUnitDisabled: false,
        uomDisabled: false,
        defaultCurrency: 'USD',
        defaultPerUnit: 1,
        defaultUoM: 'EA'  // Default EA for Quantity
      },
      'D': { // Gross Weight
        label: 'Gross Weight',
        amountLabel: 'Amount per KG',
        amountPlaceholder: 'e.g., 2.50',
        amountSuffix: null,
        currencyDisabled: false,
        perUnitDisabled: false,
        uomDisabled: false,
        defaultCurrency: 'USD',
        defaultPerUnit: 1,
        defaultUoM: 'KG'  // Default KG for Gross Weight
      },
      'E': { // Net Weight
        label: 'Net Weight',
        amountLabel: 'Amount per KG',
        amountPlaceholder: 'e.g., 2.50',
        amountSuffix: null,
        currencyDisabled: false,
        perUnitDisabled: false,
        uomDisabled: false,
        defaultCurrency: 'USD',
        defaultPerUnit: 1,
        defaultUoM: 'KG'  // Default KG for Net Weight
      },
      'F': { // Volume
        label: 'Volume',
        amountLabel: 'Amount per M³',
        amountPlaceholder: 'e.g., 5.00',
        amountSuffix: null,
        currencyDisabled: false,
        perUnitDisabled: false,
        uomDisabled: false,
        defaultCurrency: 'USD',
        defaultPerUnit: 1,
        defaultUoM: 'M3'  // Default M3 for Volume
      }
    };
    
    return calcTypeConfig[calcType] || calcTypeConfig['C']; // Default to Quantity
  };

  // Render a key field input based on field definition
  // Header fields = single select, Item fields = multi-select
  const renderKeyField = (field, isHeader) => {
    const fieldId = field.field_id;
    
    // Customer field - always single select (Header level)
    if (fieldId === 'customer_id') {
      return (
        <div key={fieldId}>
          <Label className="flex items-center gap-1">
            {field.field_label}
            <span className="text-xs text-orange-600">(Single value)</span>
          </Label>
          <Select 
            value={formData.customer_id || '__NONE__'} 
            onValueChange={(v) => setFormData({...formData, customer_id: v === '__NONE__' ? '' : v})}
          >
            <SelectTrigger><SelectValue placeholder="Select Customer" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__NONE__">-- Select --</SelectItem>
              {customers.map(c => (
                <SelectItem key={c.customer_id} value={c.customer_id}>
                  {c.customer_id} - {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }
    
    // Material field - multi-select if Item level, single select if Header level
    if (fieldId === 'material_id') {
      if (isHeader) {
        // Single select for header level
        return (
          <div key={fieldId}>
            <Label className="flex items-center gap-1">
              {field.field_label}
              <span className="text-xs text-orange-600">(Single value)</span>
            </Label>
            <Select 
              value={formData.material_id || '__NONE__'} 
              onValueChange={(v) => setFormData({...formData, material_id: v === '__NONE__' ? '' : v})}
            >
              <SelectTrigger><SelectValue placeholder="Select Material" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__NONE__">-- Select --</SelectItem>
                {materials.map(m => (
                  <SelectItem key={m.material_number} value={m.material_number}>
                    {m.material_number} - {m.description}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      } else {
        // Multi-select for item level
        const selectedMaterials = formData.material_ids || [];
        return (
          <div key={fieldId} className="col-span-2">
            <Label className="flex items-center gap-1">
              {field.field_label}
              <span className="text-xs text-green-600">(Multiple values allowed)</span>
            </Label>
            <div className="border rounded-lg p-2 bg-white">
              {/* Selected materials */}
              {selectedMaterials.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {selectedMaterials.map(matId => {
                    const mat = materials.find(m => m.material_number === matId);
                    return (
                      <span 
                        key={matId} 
                        className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded text-xs"
                      >
                        {matId} {mat ? `- ${mat.description?.substring(0, 20)}...` : ''}
                        <button
                          type="button"
                          onClick={() => setFormData({
                            ...formData, 
                            material_ids: selectedMaterials.filter(id => id !== matId)
                          })}
                          className="hover:text-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              {/* Add material dropdown */}
              <Select 
                value="__ADD__" 
                onValueChange={(v) => {
                  if (v !== '__ADD__' && !selectedMaterials.includes(v)) {
                    setFormData({...formData, material_ids: [...selectedMaterials, v]});
                  }
                }}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="+ Add Material" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ADD__">+ Add Material...</SelectItem>
                  {materials.filter(m => !selectedMaterials.includes(m.material_number)).map(m => (
                    <SelectItem key={m.material_number} value={m.material_number}>
                      {m.material_number} - {m.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                {selectedMaterials.length === 0 
                  ? 'Select one or more materials. Leave empty for all materials.' 
                  : `${selectedMaterials.length} material(s) selected`}
              </p>
            </div>
          </div>
        );
      }
    }
    
    // Sales Organization field - dropdown
    if (fieldId === 'sales_org') {
      if (isHeader) {
        return (
          <div key={fieldId}>
            <Label className="flex items-center gap-1">
              {field.field_label}
              <span className="text-xs text-orange-600">(Single value)</span>
            </Label>
            <Select 
              value={formData.sales_org || '__NONE__'} 
              onValueChange={(v) => setFormData({...formData, sales_org: v === '__NONE__' ? '' : v})}
            >
              <SelectTrigger><SelectValue placeholder="Select Sales Org" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__NONE__">-- Select --</SelectItem>
                {salesOrgs.map(so => (
                  <SelectItem key={so.sales_org_id} value={so.sales_org_id}>
                    {so.sales_org_id} - {so.sales_org_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      } else {
        // Multi-select for item level (less common but supported)
        const selectedValues = formData.sales_orgs || [];
        return renderMultiSelectDropdown(fieldId, field.field_label, 'sales_orgs', selectedValues, 
          salesOrgs.map(so => ({ id: so.sales_org_id, label: `${so.sales_org_id} - ${so.sales_org_name}` })));
      }
    }
    
    // Distribution Channel field - dropdown
    if (fieldId === 'distribution_channel') {
      if (isHeader) {
        return (
          <div key={fieldId}>
            <Label className="flex items-center gap-1">
              {field.field_label}
              <span className="text-xs text-orange-600">(Single value)</span>
            </Label>
            <Select 
              value={formData.distribution_channel || '__NONE__'} 
              onValueChange={(v) => setFormData({...formData, distribution_channel: v === '__NONE__' ? '' : v})}
            >
              <SelectTrigger><SelectValue placeholder="Select Channel" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__NONE__">-- Select --</SelectItem>
                {distributionChannels.map(dc => (
                  <SelectItem key={dc.dist_channel_id} value={dc.dist_channel_id}>
                    {dc.dist_channel_id} - {dc.dist_channel_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      } else {
        const selectedValues = formData.distribution_channels || [];
        return renderMultiSelectDropdown(fieldId, field.field_label, 'distribution_channels', selectedValues,
          distributionChannels.map(dc => ({ id: dc.dist_channel_id, label: `${dc.dist_channel_id} - ${dc.dist_channel_name}` })));
      }
    }
    
    // Division field - dropdown
    if (fieldId === 'division') {
      if (isHeader) {
        return (
          <div key={fieldId}>
            <Label className="flex items-center gap-1">
              {field.field_label}
              <span className="text-xs text-orange-600">(Single value)</span>
            </Label>
            <Select 
              value={formData.division || '__NONE__'} 
              onValueChange={(v) => setFormData({...formData, division: v === '__NONE__' ? '' : v})}
            >
              <SelectTrigger><SelectValue placeholder="Select Division" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__NONE__">-- Select --</SelectItem>
                {divisions.map(d => (
                  <SelectItem key={d.division_id} value={d.division_id}>
                    {d.division_id} - {d.division_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      } else {
        const selectedValues = formData.divisions || [];
        return renderMultiSelectDropdown(fieldId, field.field_label, 'divisions', selectedValues,
          divisions.map(d => ({ id: d.division_id, label: `${d.division_id} - ${d.division_name}` })));
      }
    }
    
    // Plant field - dropdown
    if (fieldId === 'plant') {
      if (isHeader) {
        return (
          <div key={fieldId}>
            <Label className="flex items-center gap-1">
              {field.field_label}
              <span className="text-xs text-orange-600">(Single value)</span>
            </Label>
            <Select 
              value={formData.plant || '__NONE__'} 
              onValueChange={(v) => setFormData({...formData, plant: v === '__NONE__' ? '' : v})}
            >
              <SelectTrigger><SelectValue placeholder="Select Plant" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__NONE__">-- Select --</SelectItem>
                {plants.map(p => (
                  <SelectItem key={p.plant_id} value={p.plant_id}>
                    {p.plant_id} - {p.plant_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      } else {
        const selectedValues = formData.plants || [];
        return renderMultiSelectDropdown(fieldId, field.field_label, 'plants', selectedValues,
          plants.map(p => ({ id: p.plant_id, label: `${p.plant_id} - ${p.plant_name}` })));
      }
    }
    
    // Material Group field - dropdown
    if (fieldId === 'material_group') {
      if (isHeader) {
        return (
          <div key={fieldId}>
            <Label className="flex items-center gap-1">
              {field.field_label}
              <span className="text-xs text-orange-600">(Single value)</span>
            </Label>
            <Select 
              value={formData.material_group || '__NONE__'} 
              onValueChange={(v) => setFormData({...formData, material_group: v === '__NONE__' ? '' : v})}
            >
              <SelectTrigger><SelectValue placeholder="Select Material Group" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__NONE__">-- Select --</SelectItem>
                {materialGroups.map(mg => (
                  <SelectItem key={mg.material_group_id} value={mg.material_group_id}>
                    {mg.material_group_id} - {mg.description}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      } else {
        const selectedValues = formData.material_groups || [];
        return renderMultiSelectDropdown(fieldId, field.field_label, 'material_groups', selectedValues,
          materialGroups.map(mg => ({ id: mg.material_group_id, label: `${mg.material_group_id} - ${mg.description}` })));
      }
    }
    
    // Fallback for other fields - text input
    const fieldMapping = {
      'customer_group': { single: 'customer_group', multi: 'customer_groups' },
      'price_list': { single: 'price_list', multi: 'price_lists' }
    };
    
    const mapping = fieldMapping[fieldId] || { single: fieldId, multi: `${fieldId}s` };
    
    if (isHeader) {
      // Single value input for header fields
      return (
        <div key={fieldId}>
          <Label className="flex items-center gap-1">
            {field.field_label}
            <span className="text-xs text-orange-600">(Single value)</span>
          </Label>
          <Input
            value={formData[mapping.single] || ''}
            onChange={(e) => setFormData({...formData, [mapping.single]: e.target.value})}
            placeholder={`Enter ${field.field_label}`}
          />
        </div>
      );
    } else {
      // Multi value input for item fields
      const selectedValues = formData[mapping.multi] || [];
      const singleValue = formData[mapping.single] || '';
      
      return (
        <div key={fieldId}>
          <Label className="flex items-center gap-1">
            {field.field_label}
            <span className="text-xs text-green-600">(Multiple values)</span>
          </Label>
          <div className="border rounded-lg p-2 bg-white">
            {/* Selected values */}
            {selectedValues.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {selectedValues.map((val, idx) => (
                  <span 
                    key={idx} 
                    className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded text-xs"
                  >
                    {val}
                    <button
                      type="button"
                      onClick={() => setFormData({
                        ...formData, 
                        [mapping.multi]: selectedValues.filter((_, i) => i !== idx)
                      })}
                      className="hover:text-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            {/* Add value input */}
            <div className="flex gap-2">
              <Input
                value={singleValue}
                onChange={(e) => setFormData({...formData, [mapping.single]: e.target.value})}
                placeholder={`Enter ${field.field_label}`}
                className="flex-1 h-8 text-sm"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && singleValue.trim()) {
                    e.preventDefault();
                    if (!selectedValues.includes(singleValue.trim())) {
                      setFormData({
                        ...formData, 
                        [mapping.multi]: [...selectedValues, singleValue.trim()],
                        [mapping.single]: ''
                      });
                    }
                  }
                }}
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8"
                onClick={() => {
                  if (singleValue.trim() && !selectedValues.includes(singleValue.trim())) {
                    setFormData({
                      ...formData, 
                      [mapping.multi]: [...selectedValues, singleValue.trim()],
                      [mapping.single]: ''
                    });
                  }
                }}
              >
                <Plus className="w-3 h-3" />
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {selectedValues.length === 0 
                ? 'Enter value and press Enter or click + to add multiple' 
                : `${selectedValues.length} value(s) added`}
            </p>
          </div>
        </div>
      );
    }
  };
  
  // Helper function to render multi-select dropdown for item-level fields
  const renderMultiSelectDropdown = (fieldId, label, stateField, selectedValues, options) => {
    return (
      <div key={fieldId}>
        <Label className="flex items-center gap-1">
          {label}
          <span className="text-xs text-green-600">(Multiple values)</span>
        </Label>
        <div className="border rounded-lg p-2 bg-white">
          {/* Selected values */}
          {selectedValues.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {selectedValues.map((val, idx) => {
                const option = options.find(o => o.id === val);
                return (
                  <span 
                    key={idx} 
                    className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded text-xs"
                  >
                    {option ? option.label : val}
                    <button
                      type="button"
                      onClick={() => setFormData({
                        ...formData, 
                        [stateField]: selectedValues.filter((_, i) => i !== idx)
                      })}
                      className="hover:text-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
          {/* Add dropdown */}
          <Select 
            value="__ADD__" 
            onValueChange={(v) => {
              if (v !== '__ADD__' && !selectedValues.includes(v)) {
                setFormData({...formData, [stateField]: [...selectedValues, v]});
              }
            }}
          >
            <SelectTrigger className="h-8 text-sm">
              <SelectValue placeholder="+ Add value" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__ADD__">+ Add value...</SelectItem>
              {options.filter(o => !selectedValues.includes(o.id)).map(o => (
                <SelectItem key={o.id} value={o.id}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-500 mt-1">
            {selectedValues.length === 0 
              ? 'Select one or more values. Leave empty for all.' 
              : `${selectedValues.length} value(s) selected`}
          </p>
        </div>
      </div>
    );
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [
        typesRes, tablesRes, seqRes, matsRes, custRes,
        salesOrgsRes, distChannelsRes, divisionsRes, plantsRes, matGroupsRes
      ] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/pricing/condition-types`),
        axios.get(`${BACKEND_URL}/api/pricing/condition-tables`),
        axios.get(`${BACKEND_URL}/api/pricing/access-sequences`),
        axios.get(`${BACKEND_URL}/api/materials`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/customers`).catch(() => ({ data: [] })),
        // Additional master data for dropdowns
        axios.get(`${BACKEND_URL}/api/sales-distribution/sales-organizations`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/sales-distribution/distribution-channels`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/sales-distribution/divisions`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/material-management/plants`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/material-groups`).catch(() => ({ data: [] }))
      ]);
      setConditionTypes(typesRes.data || []);
      setConditionTables(tablesRes.data || []);
      setAccessSequences(seqRes.data || []);
      setMaterials(matsRes.data || []);
      setCustomers(custRes.data || []);
      // Set additional master data
      setSalesOrgs(salesOrgsRes.data || []);
      setDistributionChannels(distChannelsRes.data || []);
      setDivisions(divisionsRes.data || []);
      setPlants(plantsRes.data || []);
      setMaterialGroups(matGroupsRes.data || []);
    } catch (error) {
      console.error('Error loading master data:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to load master data: ${errorMsg}`);
    } finally {
      setIsLoading(false);
    }
  };

  const loadRecords = async () => {
    try {
      const url = filterType 
        ? `${BACKEND_URL}/api/pricing/condition-records?condition_type_id=${filterType}`
        : `${BACKEND_URL}/api/pricing/condition-records`;
      const response = await axios.get(url);
      setConditionRecords(response.data || []);
    } catch (error) {
      console.error('Error loading condition records:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to load condition records: ${errorMsg}`);
    }
  };

  const handleOpenDialog = (record = null) => {
    if (record) {
      setEditingRecord(record);
      setFormData({
        condition_type_id: record.condition_type_id,
        condition_table_id: record.condition_table_id,
        sales_org: record.sales_org || '',
        distribution_channel: record.distribution_channel || '',
        customer_id: record.customer_id || '',
        material_id: record.material_id || '',
        material_ids: record.material_ids || (record.material_id ? [record.material_id] : []),
        material_group: record.material_group || '',
        material_groups: record.material_groups || (record.material_group ? [record.material_group] : []),
        plant: record.plant || '',
        plants: record.plants || (record.plant ? [record.plant] : []),
        condition_amount: record.condition_amount,
        condition_unit: record.condition_unit || '',
        condition_currency: record.condition_currency || 'USD',
        per_unit: record.per_unit || 1,
        unit_of_measure: record.unit_of_measure || 'EA',
        has_scales: record.has_scales || false,
        scale_basis: record.scale_basis || 'B',
        scale_lines: record.scale_lines || [],
        valid_from: record.valid_from?.split('T')[0] || new Date().toISOString().split('T')[0],
        valid_to: record.valid_to?.split('T')[0] || '2099-12-31',
        is_active: record.is_active
      });
    } else {
      setEditingRecord(null);
      setFormData({
        condition_type_id: filterType || '',
        condition_table_id: '',
        sales_org: '',
        distribution_channel: '',
        customer_id: '',
        material_id: '',
        material_ids: [],
        material_group: '',
        material_groups: [],
        plant: '',
        plants: [],
        condition_amount: '',
        condition_unit: '',
        condition_currency: 'USD',
        per_unit: 1,
        unit_of_measure: 'EA',
        has_scales: false,
        scale_basis: 'B',
        scale_lines: [],
        valid_from: new Date().toISOString().split('T')[0],
        valid_to: '2099-12-31',
        is_active: true
      });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.condition_type_id) {
      toast.error('Condition Type is required');
      return;
    }
    if (!formData.condition_table_id) {
      toast.error('Condition Table is required');
      return;
    }
    if (!formData.condition_amount || isNaN(parseFloat(formData.condition_amount))) {
      toast.error('Valid condition amount is required');
      return;
    }
    if (!formData.valid_from || !formData.valid_to) {
      toast.error('Validity dates are required');
      return;
    }
    if (new Date(formData.valid_from) > new Date(formData.valid_to)) {
      toast.error('Valid From date must be before Valid To date');
      return;
    }
    
    setIsSaving(true);
    try {
      // Prepare scale lines with proper numeric types
      const cleanedScaleLines = formData.has_scales && formData.scale_lines.length > 0
        ? formData.scale_lines
            .filter(line => line.scale_quantity !== '' && line.condition_amount !== '')
            .map(line => ({
              scale_quantity: parseFloat(line.scale_quantity) || 0,
              condition_amount: parseFloat(line.condition_amount) || 0
            }))
            .sort((a, b) => a.scale_quantity - b.scale_quantity)
        : [];

      const data = {
        ...formData,
        condition_type_id: formData.condition_type_id.toUpperCase(),
        condition_amount: parseFloat(formData.condition_amount),
        per_unit: parseFloat(formData.per_unit) || 1,
        sales_org: formData.sales_org || null,
        distribution_channel: formData.distribution_channel || null,
        customer_id: formData.customer_id || null,
        // For item-level fields, use the array if populated, otherwise use single value
        material_id: formData.material_ids?.length > 0 ? formData.material_ids[0] : (formData.material_id || null),
        material_ids: formData.material_ids?.length > 0 ? formData.material_ids : null,
        material_group: formData.material_groups?.length > 0 ? formData.material_groups[0] : (formData.material_group || null),
        material_groups: formData.material_groups?.length > 0 ? formData.material_groups : null,
        plant: formData.plants?.length > 0 ? formData.plants[0] : (formData.plant || null),
        plants: formData.plants?.length > 0 ? formData.plants : null,
        condition_unit: formData.condition_unit || null,
        unit_of_measure: formData.unit_of_measure || null,
        has_scales: formData.has_scales && cleanedScaleLines.length > 0,
        scale_basis: formData.has_scales ? formData.scale_basis : null,
        scale_lines: cleanedScaleLines
      };

      if (editingRecord) {
        await axios.put(`${BACKEND_URL}/api/pricing/condition-records/${editingRecord.condition_record_id}`, data);
        toast.success(`Condition Record '${editingRecord.condition_record_id}' updated successfully`);
      } else {
        const response = await axios.post(`${BACKEND_URL}/api/pricing/condition-records`, data);
        toast.success(`Condition Record '${response.data.condition_record_id}' created successfully`);
      }
      setIsDialogOpen(false);
      loadRecords();
    } catch (error) {
      console.error('Error saving condition record:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to save condition record: ${errorMsg}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (recordId) => {
    if (!window.confirm(`Are you sure you want to delete Condition Record '${recordId}'? This action cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`${BACKEND_URL}/api/pricing/condition-records/${recordId}`);
      toast.success(`Condition Record '${recordId}' deleted successfully`);
      loadRecords();
    } catch (error) {
      console.error('Error deleting condition record:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to delete condition record: ${errorMsg}`);
    }
  };

  const getConditionType = (id) => conditionTypes.find(ct => ct.condition_type_id === id);

  const formatAmount = (record) => {
    const ct = getConditionType(record.condition_type_id);
    if (!ct) return record.condition_amount;
    
    if (ct.calculation_type === 'A') {
      return `${record.condition_amount}%`;
    } else if (ct.calculation_type === 'B') {
      return `${record.condition_currency} ${record.condition_amount}`;
    } else {
      return `${record.condition_currency} ${record.condition_amount} / ${record.per_unit} ${record.unit_of_measure || 'EA'}`;
    }
  };

  const getTableDescription = (tableId) => {
    const table = conditionTables.find(t => t.condition_table_id === tableId);
    return table?.description || tableId;
  };

  // Bag of deps shared with the extracted dialog component
  const ctx = {
    isDialogOpen, setIsDialogOpen, editingRecord,
    formData, setFormData, isSaving,
    conditionTypes, conditionTables, accessSequences,
    handleSubmit,
    getFilteredConditionTables, getKeyFieldsForTable,
    getCalculationTypeInfo, renderKeyField,
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Condition Records</h1>
            <p className="text-sm text-gray-600 mt-1">Maintain prices, discounts, and other condition values</p>
          </div>
          {writable && (<Button onClick={() => handleOpenDialog()} data-testid="add-condition-record-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Condition Record
          </Button>)}
        </div>

        {/* Filter */}
        <Card className="p-4 mb-6">
          <div className="flex items-center gap-4">
            <Search className="w-5 h-5 text-gray-400" />
            <div className="w-64">
              <Select value={filterType || '__ALL__'} onValueChange={(v) => setFilterType(v === '__ALL__' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by Condition Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ALL__">All Condition Types</SelectItem>
                  {conditionTypes.map(ct => (
                    <SelectItem key={ct.condition_type_id} value={ct.condition_type_id}>
                      {ct.condition_type_id} - {ct.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <span className="text-sm text-gray-500">{conditionRecords.length} records found</span>
          </div>
        </Card>

        <Card className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
              <span className="ml-3 text-gray-600">Loading condition records...</span>
            </div>
          ) : conditionRecords.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500">No condition records found.</p>
              <p className="text-sm text-gray-400 mt-1">Click "Create Condition Record" to add pricing data.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="condition-records-table">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Record ID</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Cond. Type</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Table</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Customer</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Material</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">Amount</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Validity</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {conditionRecords.map((record) => (
                    <tr key={record.condition_record_id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 font-mono text-sm">{record.condition_record_id}</td>
                      <td className="py-3 px-4">
                        <span className="font-bold">{record.condition_type_id}</span>
                        <span className="text-gray-500 text-sm ml-2">
                          {getConditionType(record.condition_type_id)?.description}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm">{getTableDescription(record.condition_table_id)}</td>
                      <td className="py-3 px-4">{record.customer_id || '-'}</td>
                      <td className="py-3 px-4">{record.material_id || '-'}</td>
                      <td className="py-3 px-4 text-right">
                        <div className="font-mono font-bold text-green-700">
                          {formatAmount(record)}
                        </div>
                        {record.has_scales && (
                          <div className="flex items-center justify-end gap-1 mt-1">
                            <Layers className="w-3 h-3 text-orange-500" />
                            <span className="text-xs text-orange-600">Scale</span>
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3 text-gray-400" />
                          {record.valid_from?.split('T')[0]} - {record.valid_to?.split('T')[0]}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          record.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {record.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex justify-end gap-1">
                          <RecordActions doctype="pricing" onView={() => setViewItem(record)} onEdit={() => handleOpenDialog(record)} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <ConditionRecordDialog ctx={ctx} />
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default ConditionRecords;
